import type { GridConnectionMode, ProjectIntent, SiteRagReport } from '../../types/siteRag';
import { analysisReadinessDecisionV1Schema } from '../../types/decisionAnalysis';
import {
  deriveScreeningArchitectureRecommendations,
  type ScreeningArchitectureRecommendation,
} from '../findings/screeningArchitectureRecommendations';
import { applyCoolingProposal, applyPhasePowerProposal } from '../stage2/applyStage2Choice';
import type { BtmStrategy } from '../stage2/stage2Types';
import { undecidedProjectIntent } from './undecidedProjectIntent';

/** This chooses the screening workflow, without clearing or adopting a proposal. */
export function isScreeningRecommendationMode(intent: ProjectIntent | null | undefined): boolean {
  return intent?.screeningMode === 'recommend';
}

export function withScreeningIntentMode(intent: ProjectIntent | undefined,
  screeningMode: NonNullable<ProjectIntent['screeningMode']>): ProjectIntent {
  return { ...(intent ?? undecidedProjectIntent()), screeningMode };
}

export interface ScreeningIntentChoice {
  id: 'cooling' | 'phase-power' | 'btm-strategy';
  label: string;
  qualification: string;
  nextIntent?: ProjectIntent;
  strategy?: BtmStrategy;
}

export function canApplyScreeningRecommendations(report: SiteRagReport | null, siteId: string): boolean {
  if (!report || report.siteId !== siteId || report.degradedMode || report.requiresRegeneration) return false;
  const parsed = analysisReadinessDecisionV1Schema.safeParse(report.analysisReadiness ?? report.analysisRevision?.readiness);
  return parsed.success && parsed.data.ready && parsed.data.canUseForDecision && parsed.data.current
    && Object.keys(deriveScreeningArchitectureRecommendations(report, siteId)).length > 0;
}

/**
 * The phase connection mode a saved screening architecture line supports.
 * 'islanded' only when the saved text explicitly says islanded; 'BTM-only' /
 * 'behind-the-meter only' is a meter arrangement (BTM generation can run in
 * parallel with the grid), so it stays 'undecided' rather than becoming an
 * islanding declaration that would drop the grid rows from the call-ready brief.
 */
export function screeningPhaseGridConnection(
  power: Pick<ScreeningArchitectureRecommendation, 'strategy' | 'islanded'> | undefined,
): GridConnectionMode {
  if (power?.strategy === 'islanded') return 'islanded';
  if (power?.strategy === 'btm-only') return power.islanded === true ? 'islanded' : 'undecided';
  return power?.strategy === 'grid-only' || power?.strategy === 'hybrid' ? 'grid-connected' : 'undecided';
}

/** Adoption is stricter than historical viewing: only a current eligible
 * completed screen can supply choices. Open fields use the existing Stage 2
 * transforms; no demand, date, rating, phase or engineering authority is created. */
export function screeningIntentChoices(report: SiteRagReport | null, siteId: string,
  intent: ProjectIntent, strategy?: BtmStrategy): ScreeningIntentChoice[] {
  if (!canApplyScreeningRecommendations(report, siteId)) return [];
  const advice = deriveScreeningArchitectureRecommendations(report, siteId);
  const choices: ScreeningIntentChoice[] = [];
  const cooling = advice['cooling-strategy'];
  const sink = advice['cooling-type'];
  const coolingProposal = {
    kind: 'cooling' as const,
    ...(cooling?.coolingStrategy ? { coolingStrategy: cooling.coolingStrategy } : {}),
    ...(sink?.heatRejection ? { bopHeatRejection: sink.heatRejection } : {}),
    summary: [cooling?.value, sink?.value].filter(Boolean).join('; '), reasons: [],
  };
  const cooled = applyCoolingProposal(intent, coolingProposal);
  if (cooled !== intent) choices.push({ id: 'cooling', label: 'Apply open cooling choices to Intent',
    qualification: [cooling?.basisNote, sink?.basisNote].filter(Boolean).join(' '), nextIntent: cooled });
  const power = advice['btm-architecture'];
  if (power?.strategy && strategy == null) choices.push({ id: 'btm-strategy', label: 'Apply BTM strategy to Intent',
    qualification: power.basisNote, strategy: power.strategy });
  if (power?.supplyOptions?.length && intent.phases?.length) {
    const options = power.supplyOptions;
    const next = applyPhasePowerProposal(intent, {
      kind: 'phase-power', summary: power.value, reasons: [],
      phases: intent.phases.map(phase => ({ phaseId: phase.id, phaseLabel: phase.label,
        options: [...options], battery: false,
        gridConnection: screeningPhaseGridConnection(power) })),
    });
    if (next !== intent) choices.push({ id: 'phase-power', label: 'Apply open phase power choices to Intent',
      qualification: `${power.basisNote} These are chosen planning options for existing open phases; phase engineering remains unresolved.`, nextIntent: next });
  }
  return choices;
}
